import { DateTime } from "luxon";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4, NIL as uuidNull } from "uuid";
import { DocumentKey, IDocumentUpdateFormProps } from "../../../../@types/documents";
import { Button } from "../../../../components/buttons/button";
import { SettingsButtonModal } from "../../../../components/buttons/iconButtons/settingsButton/SettingsButtonModal";
import { Accordion } from "../../../../components/controls/accordion";
import { DictionaryInput } from "../../../../components/controls/dictionaryInput";
import GridSelectorInput from "../../../../components/controls/GridSelectorInput";
import GridWrapper from "../../../../components/controls/GridWrapper";
import { DateInput, TextInput } from "../../../../components/controls/inputs";
import BaseDocumentCreator from "../../../../components/documents/baseDocumentCreator";
import { IOption, Select } from "../../../../components/selects/select";
import { IEntitySimpleDTO } from "../../../../libs/coreapi-dto/@types/common";
import { IGoodsDTO } from "../../../../libs/coreapi-dto/dirs/goods";
import { IInvoiceCreateDTO, IInvoiceGetDTO } from "../../../../libs/coreapi-dto/documents/invoice";
import { IInvoiceItemCreateDTO, IInvoiceItemGetDTO } from "../../../../libs/coreapi-dto/documents/invoiceItem";
import { ContractorDataProvider } from "../../../../Services/DataProviders/ContractorDataProvider";
import { GoodsDataProvider } from "../../../../Services/DataProviders/GoodsDataProvider";
import { PricingInvoiceDataProvider } from "../../../../Services/DataProviders/PricingInvoiceDataProvider";
import { useForm } from "../../../../system/hooks/useForm";
import useGridFilter, { GridFilterAction } from "../../../../system/hooks/useGridFilter";
import { useAppContext } from "../../../../system/providers/appContextProvider";
import { useTabsContext } from "../../../../system/providers/tabsProvider";
import { ContractorSelectorModal } from "../../../Dictionaries/Contractor";
import { GoodsSelectorModal } from "../../../Dictionaries/Goods";
import KizScanModal from "../../../Dictionaries/Kiz/ScanModal/KizScanModal";
import { PricingModelSelectorModal } from "../../../Dictionaries/PricingModel";
import { IKeyValuePair } from "../../../Reports/Shared/Interfaces/IKeyValuePair";
import { InvoiceCreatorItemGridPluginSettings } from "./InvoiceItemCreator/InvoiceItemCreatorGridPluginSettings";
import styles from "../styles/InvoiceCreatorView.module.scss";
import stylesIndex from '../../../styles/index.module.scss'
import { DocumentType, EncodingType, IdTableVariant, SearchOption, SysOptionName } from "../../../../@types/enumsGlobal";
import InvoiceGoodsSelectModal from "./InvoiceGoodsSelectModal";
import { IGoodsByIdDTO } from "../../../../libs/core-api-requests/dirs/goodsRequest";
import PositionEditPanel from "../../../../components/commandsPanels/PositionEditPanel";
import { MessageModalWindow } from "../../../../components/modalWindows/MessageModalWindow";
import { StockBalanceButtonCompress, StockBalanceButtonExpand } from "../../../../components/buttons/iconButtons/special/QuickAccessBarCollection";
import Tooltip from "../../../../components/tooltips/Tooltip";
import { IScalingRatioDTO } from "../../../../libs/coreapi-dto/dirs/scalingRatio";
import { ScalingRatioDataProvider } from "../../../../Services/DataProviders/ScalingRatioDataProvider";
import { autocomplete } from "../../../../system/functions/autocomplete";
import { getStyleCell } from "../../../../system/functions/getStyleCell";
import { kizCounter, sumKizsCount } from "../../../../system/functions/sumKiszCount";
import { ChangeableGrid, IChangeDataGrid } from "../../../../components/grids/changeableGrid/ChangeableGrid";
import collectPricingInvoiceItemDTO, { ICollectPricingInvoiceItemProps } from "../CollectPricingInvoiceItemDTO";
import { KizDataProvider } from "../../../../Services/DataProviders/KizDataProvider";
import { BarcodeType, IKizBoxDTO, IKizDTO, IkizDocumentItemGtinDto } from "../../../../libs/coreapi-dto/dirs/kiz";
import renderGlobalAlert from "../../../../system/hooks/useGlobalAlert";
import { addKizOrKizBox } from "../../../../system/functions/addKizOrKizBox";
import { CheckBox } from "../../../../components/controls/checkbox";
import { StoreSelectorPanel } from "../../../../components/storeSelectorPanel";
import { MoneyFormatComponent } from "../../../../components/controls/inputs/BaseInput";
import { GosContractSelectorModal } from "../../../Dictionaries/GosContract/components/GosContractSelectorModal";
import { addValueToArray } from "../../../../system/functions/addValToArray";
import { GosContractDataProvider } from "../../../../Services/DataProviders/GosContractDataProvider";
import { IGosContractDTO, IGosContractGoodsDTO } from "../../../../libs/coreapi-dto/dirs/gosContract";
import { validateDuplicateKizs } from "../../../../system/functions/validateDuplicateKizs";
import { useTimeout } from "../../../../system/hooks/useTimeout";
import { useComPortScanner } from "../../../../system/hooks/useHubConnection";
import FlexRow from "../../../../components/controls/FlexRow";
import useLockingDocuments from "../../../../components/lockDocuments/useLockingDocuments";
import { IContractorDTO } from "../../../../libs/coreapi-dto/dirs/contractor";
import { checkLockStatus } from "../../../CommonHelperFunctions";
import { useUserContext } from "../../../../system/providers/userContextProvider";
import { InvoiceItemsSelectorModal } from "../InvoiceItem/InvoiceItemsSelectorModal";
import { IBaseDocumentItemKizDTO } from "../../../../libs/coreapi-dto/documents/base";
import { GosContractSearchSelectorModal } from "../../../Dictionaries/GosContract/components/GosContractSearchSelectorModal";
import useItemsByGtin from "../../../../system/hooks/useItemsByGtin";
import useAutoSaveDocument from "../../../../system/hooks/useAutoSaveDocument";
import { SysOptionsDataProvider } from "../../../../Services/DataProviders/SysOptionsDataProvider";
import { GosContractGoodsSelectorModal } from "../../../Dictionaries/GosContract/components/GosContractGoodsSelectorModal";
import { checkCapsLock } from "../../../../system/functions/capsLockHelper";
import { useInvoiceItemTabSettings } from "./InvoiceItemCreator/useInvoiceItemTabSettings";
import { TabIndexSettingsButtonModal } from "../../../../components/buttons/iconButtons/tabIndexSettingsButton/TabIndexSettingsButton";

interface ISum {
    amountRetailSum: number;
    amountRetailVatSum: number;
    amountRetailSumIncVat: number;
    amountSupplierSum: number;
    amountSupplierVatSum: number;
    amountSupplierSumIncVat: number;
}

interface IGoodsViewItem {
    item: IGoodsByIdDTO[] | null;
    show: boolean;
}

interface IStoreModel {
    store?: IEntitySimpleDTO;
}

interface IValidator {
    supplier: IEntitySimpleDTO;
    pricingModel: IEntitySimpleDTO;
    documentDate: DateTime;
}

interface IMessageModalProps {
    show: boolean;
    message: string;
    btSaveOrOk?: BtSaveOrOk;
}

interface IGosContractGoodsModal extends IMessageModalProps {
    entity?: IEntitySimpleDTO
    referentPrice?: boolean;
    gosContractGoods?: IGosContractGoodsDTO[];
    idGosContractGlobal?: string
    idGoodsGlobal?: string
}

export type BtSaveOrOk = "ok" | "save";

export interface IDisplayItem {
    idGlobal: string;
    goodsName: string;
    scalingRatioName: string;
    quantity: number;

    productMargin: number;
    producerPrice: number;

    retailPrice: number;
    retailPriceIncVat: number;
    retailAmount: number;
    retailAmountIncVat: number;
    retailVat: number;

    supplierPrice: number;
    supplierPriceIncVat?: number;
    supplierAmount: number;
    supplierAmountIncVat: number;
    gtin?: string;
    supplierVat: number;

    isKiz: boolean;
    isGnvls: boolean;
    countKizScan: number | undefined;
    numerator?: number;
    denominator?: number;
    goodsCode: string;
    producer: string;

    absentInGC?: boolean | null;
    idInvoiceItemGroupGlobal?: string | null;
    dateCreated: DateTime;
    seriesNumber: string;
    bestBefore: DateTime;
    isOsu?: boolean;
    actualQuantity?: number,
    damageQuantity?: number,
    defectQuantity?: number,
    shortageQuantity?: number
}

interface IGoodsViewItem {
    item: IGoodsByIdDTO[] | null;
    show: boolean;
}

export interface IGosContractFields {
    isGosContract: boolean;    
    referentPrice?: boolean;
    gosContractGoods: IGosContractGoodsDTO | null;
    idGosContractGlobal: string | null
}

interface IErrors {
    itemName?: string;
    producerPrice: number;
    registerPrice: number;
    message: string;
}

const InvoiceCreatorForm: FC<IDocumentUpdateFormProps<IInvoiceGetDTO, IInvoiceItemGetDTO[], IInvoiceCreateDTO>> = (props) => {
    const appContext = useAppContext();
    const tabsContext = useTabsContext();
    const userContext = useUserContext();

    const lockFromPermission = checkLockStatus(props.permission as IPermission, userContext.userPermission);
    const [onlyRead, setOnlyRead] = useState((props.variant === 'edit' && (props.document?.documentState === 'proc' || lockFromPermission)) ? true : false);

    const idTable = IdTableVariant.Invoice
    const documentType = DocumentType.invoice
    const itemsByGtin = useItemsByGtin(idTable,documentType)
    const itemsDocumentByGtin = itemsByGtin.getKizIdItemsByGtin();

    const { t } = useTranslation();
    const baseT = (value: string) => t("documents.invoice." + value);
    const errorsT = (value: string) => t("errors." + value);



    const goodsDP = new GoodsDataProvider(appContext.coreApiService);
    const contractorDP = new ContractorDataProvider(appContext.coreApiService);
    const kizDP = new KizDataProvider(appContext.coreApiService);
    const pricingInvoiceDP = new PricingInvoiceDataProvider(appContext.coreApiService);
    const scalingRatioDP = new ScalingRatioDataProvider(appContext.coreApiService);
    const gosContractDP = new GosContractDataProvider(appContext.coreApiService);
    const contractorDataProvider = new ContractorDataProvider(appContext.coreApiService);
    const sysOptionsDataProvider = new SysOptionsDataProvider(appContext.coreApiService);
    
    InvoiceCreatorItemGridPluginSettings.columns.filter(x=> x.propertyName === 'quantity').forEach((x) => {
        x.changeable = !props.document?.isHardUnProcess
    })

    const lockingDocuments = useLockingDocuments();
    const [idLockingDocument, setIdLockingDocument] = useState<string|undefined>();

    const [data, setData] = useState<IInvoiceCreateDTO>(
        props.documentUpdate ??
            ({
                idGlobal: uuidv4(),
                documentDate: props.isTest ? DateTime.now().plus({ days: -7 }) : DateTime.now(),
            } as IInvoiceCreateDTO)
    );
    
    const [displayItems, setDisplayItems] = useState<IDisplayItem[]>([]);

    const selectedItemRef = useRef<IGridRow>();
    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const [selectedItemCreateDto, setSelectedItemCreateDto] = useState<IInvoiceItemCreateDTO>();

    const [gridFilter, dispatchGridFilter] = useGridFilter();
    const [averageMarkup, setAverageMarkup] = useState<string>(recalculateAverageMarkup(data.items)); // средняя наценка
    const [mnemocode] = useState<string>(props.document?.mnemocode ?? "");
    const [sum, setSum] = useState<ISum>({
        amountRetailSum: 0,
        amountRetailVatSum: 0,
        amountRetailSumIncVat: 0,
        amountSupplierSum: 0,
        amountSupplierSumIncVat: 0,
        amountSupplierVatSum: 0,
    });
    const [showKizScan, setShowKizScan] = useState<boolean>(false);
    const [showMessageModal, setShowMessageModal] = useState<IMessageModalProps>({ show: false, message: "" });
    const [showMessageModalValidDocument, setShowMessageModalValidDocument] = useState<IMessageModalProps>({ show: false, message: "" });
    const [showMessageModalGosContractGoods, setShowMessageModalGosContractGoods] = useState<IGosContractGoodsModal>({ show: false, message: "", });
    const defaultSearchOption = {displayName: baseT("name"), value: "name"} as IOption;
    const [searchOption, setSearchOption] = useState<IOption>(defaultSearchOption);

    const [goodsViewItem, setGoodsViewItem] = useState<IGoodsViewItem>({ item: null, show: false });

    const [isCompact, setIsCompact] = useState<boolean>(false);
    const [isCompactStore, setIsCompactStore] = useState<boolean>(false);

    const [scanFocus, setScanFocus] = useState<boolean>(false);
    const [scanValue, setScanValue] = useState<string>("");
    const timeout = useRef<NodeJS.Timeout>();
    const currentIdTab = useRef<string>('');
    const [setTimer, clearTimer]  = useTimeout()

    const [store, setStore] = useState<IEntitySimpleDTO | null>(props.document?.store ?? null);
    const [pharmacy] = useState<IEntitySimpleDTO | null>(null);
    const [pricingModel, setPricingModel] = useState<IEntitySimpleDTO | null>(props.document?.pricingModel ?? null);
    const [supplier, setSupplier] = useState<IEntitySimpleDTO | null>(props.document?.supplier ?? null);
    //Validations
    const [itemsValidation, setItemsValidation] = useState<IValidationItem[]>([]);
    const [itemsFilter, setItemsFilter] = useState<IItemFilter[]>([]);

    const [sysOptionsKizValidationType, setSysOptionsKizValidationType] = useState<string>();

    const [currentSupplier, setCurrentSupplier] = useState<IContractorDTO>();
    const [currentPharmacy, setCurrentPharmacy] = useState<IContractorDTO>();
    const [kizScanModal, setKizScanModal] = useState(<></>);

    const [gosContract, setGosContract] = useState<IEntitySimpleDTO | null>(null);
    const { showSettings, SettingsModal, getBooleanSetting } = useInvoiceItemTabSettings();
    const hasItemGosContract = (items: IInvoiceItemCreateDTO[]) => {
        if (!Array.isArray(items)) {
            return false;
        }
        return items.some(item => item?.idGosContractGoodsExternalGlobal);
    }
    const disabledGosContract = hasItemGosContract(data.items) && getBooleanSetting('disabledFieldForGC')
    const showLastSearchQuery = getBooleanSetting('showLastSearchQuery')
    const coloredRowIsGnvls = getBooleanSetting('coloredRowIsGnvls')
    
    const lastSearchQuerySettings = {
        showLastSearchQuery: showLastSearchQuery,
        searchOption: searchOption,
        documentKey: 'invoiceItemCreator' as DocumentKey,
        fieldName: 'inputAccompanyingSelectionGoods'
    };

    const [errorSelectGoods, setErrorSelectGoods] = useState<string | undefined>();

    const gosContractMap = useMemo(() => {
        const map = new Map<string, boolean>();
        
        data?.items?.forEach((item: IInvoiceItemCreateDTO) => {
            const hasGosContract = 
                item.idGosContractGoodsExternalGlobal !== undefined &&
                item.idGosContractGoodsExternalGlobal !== null &&
                item.idGosContractGoodsExternalGlobal !== '';
            
            map.set(item.idGlobal, hasGosContract);
        });
        
        return map;
    }, [data?.items]);

    const firstUpdate = useRef(true);
    useEffect(() => {
        if (firstUpdate.current) {
            firstUpdate.current = false;
          } else {
            if (props.documentUpdate)
            setData(props.documentUpdate)
          }
    },[props.documentUpdate])

    useEffect(() => setData({ ...data, idStoreGlobal: store?.idGlobal }), [store]);
    useEffect(() => setData({ ...data, idPricingModelGlobal: pricingModel?.idGlobal }), [pricingModel]);
    useEffect(() => setData({ ...data, idSupplierGlobal: supplier?.idGlobal }), [supplier]);


    useEffect(() => setStore(props.document?.store ? { ...props.document?.store } : null), [props.document?.store]);
    useEffect(() => setPricingModel(props.document?.pricingModel ? { ...props.document?.pricingModel } : null), [props.document?.pricingModel]);

    useEffect(() => {
        if (props.document?.gosContract) setGosContract(props.document.gosContract);
    }, [props.document?.gosContract]);

    useEffect(() => {
        if (!data.idPricingModelGlobal) return;
    }, [data.idPricingModelGlobal]);


    useEffect(() => {
        if (supplier) {
            return;
        }

        if (props.document?.supplier) {
            setSupplier({ ...props.document?.supplier });
        } else {
            autocomplete().then((e) => {
                e.defaultSupplier && setSupplier({ idGlobal: e.defaultSupplier.idGlobal, displayName: e.defaultSupplier.name });
            });
        }
    }, [props.document?.supplier]);

    useEffect(() => {
        if (pricingModel !== null) {
            return;
        }
        if (pharmacy !== null) {
            contractorDP.getById(pharmacy.idGlobal, (contractor) => {
                setPricingModel(contractor.pricingPricingModel);
                setCurrentPharmacy(contractor);
            });
        }
    }, [pharmacy]);

    useEffect(() => {
        if (supplier) {
            contractorDataProvider.getById(supplier.idGlobal, (dataRequest) => setCurrentSupplier(dataRequest));
        }
    }, [supplier])

    useEffect(() => {
        const handleTabClose = (event) => {
            lockingDocuments.delete({
                idTable: idTable,
                idDocument: data.idGlobal as string,
            });
            return (event.returnValue = "");
        };

        window.addEventListener("beforeunload", handleTabClose);

        return () => {
            window.removeEventListener("beforeunload", handleTabClose);
        };
    }, []);

    // Блокировка документа
    const requestUpdateLockingDocumentTimeout = useRef<NodeJS.Timeout>();
    const isUpdatingLockingDocument = useRef(false);

    useEffect(() => {
        // Если документ не определён — ничего не делаем
        if (!idLockingDocument) return;

        // Если уже идёт обновление — не ставим новый таймер
        if (isUpdatingLockingDocument.current) return;

        // Отменяем предыдущий таймер
        if (requestUpdateLockingDocumentTimeout.current) {
            clearTimeout(requestUpdateLockingDocumentTimeout.current);
        }

        clearTimeout(requestUpdateLockingDocumentTimeout.current as NodeJS.Timeout);
        requestUpdateLockingDocumentTimeout.current = setTimeout(() => {

            isUpdatingLockingDocument.current = true;

            lockingDocuments.update(idLockingDocument, (e) => {
                isUpdatingLockingDocument.current = false;
            },
                () => {
                    isUpdatingLockingDocument.current = false;
                })
        }, Number(process.env.REACT_APP_INTERVAL_REQUEST));
    }, [data.documentDate, data.idPricingModelGlobal, data.idStoreGlobal, data.idSupplierGlobal, data.items]);

    useEffect(() => {
        if (tabsContext?.currentTab) {
            currentIdTab.current = tabsContext.currentTab?.id;
        }
    }, []);

    useComPortScanner((data) => {
        if (tabsContext.currentTab && (tabsContext.currentTab?.id === currentIdTab.current)) {
            if (!showKizScan && selectedItem) {
                setScanValue(data);
                addKiz(data, true);
            }
        }
    })

    const addKiz = (scanValue: string, portScanner?: boolean) => {
        clearTimeout(timeout.current as NodeJS.Timeout);
        timeout.current = setTimeout(() => {
            let item = data?.items.find((x) => x.idGlobal === selectedItem?.idGlobal) as IInvoiceItemCreateDTO;
            if (item?.isKiz) {
                if (item.quantity > (item.kizBoxes?.length || 0 + (item.kizs?.length || 0))) {
                    addKizOrKizBox(
                        scanValue,
                        kizDP,
                        documentType,
                        (value) => {
                            let filteredItems = data.items.filter((x) => x.idGlobal !== selectedItem?.idGlobal);
                            let item = data.items.find((x) => x.idGlobal === selectedItem?.idGlobal) as IInvoiceItemCreateDTO;
                            let displayItem = displayItems.find((x) => x.idGlobal === selectedItem?.idGlobal) as IDisplayItem;
                            let filteredDisplayItems = displayItems.filter((x) => x.idGlobal !== selectedItem?.idGlobal);

                            setData({
                                ...data,
                                items: [
                                    ...filteredItems,
                                    {
                                        ...item,
                                        kizs: value.kizType === BarcodeType.Kiz ? (item.kizs ? [...item.kizs, value.kiz as IKizDTO] : [value.kiz as IKizDTO]) : item.kizs,
                                        kizBoxes:
                                            value.kizType === BarcodeType.KizBox ? (item.kizBoxes ? [...item.kizBoxes, value.kizBox as IKizBoxDTO] : [value.kizBox as IKizBoxDTO]) : item.kizBoxes,
                                    },
                                ],
                            });
                            setDisplayItems([
                                ...filteredDisplayItems,
                                {
                                    ...displayItem,
                                    countKizScan: sumKizsCount(
                                        {
                                            ...item,
                                            kizs: value.kizType === BarcodeType.Kiz ? (item.kizs ? [...item.kizs, value.kiz as IKizDTO] : [value.kiz as IKizDTO]) : item.kizs,
                                            kizBoxes:
                                                value.kizType === BarcodeType.KizBox ? (item.kizBoxes ? [...item.kizBoxes, value.kizBox as IKizBoxDTO] : [value.kizBox as IKizBoxDTO]) : item.kizBoxes,
                                        },
                                        documentType
                                    ),
                                },
                            ]);
                            setSelectedItemCreateDto({
                                ...item,
                                kizs: value.kizType === BarcodeType.Kiz ? (item.kizs ? [...item.kizs, value.kiz as IKizDTO] : [value.kiz as IKizDTO]) : item.kizs,
                                kizBoxes: value.kizType === BarcodeType.KizBox ? (item.kizBoxes ? [...item.kizBoxes, value.kizBox as IKizBoxDTO] : [value.kizBox as IKizBoxDTO]) : item.kizBoxes,
                            });
                            setScanValue("");
                        },
                        portScanner
                    );
                } else {
                    setScanValue("");
                    renderGlobalAlert({
                        variant: "exception",
                        title: errorsT("boxQuantity"),
                        statusCode: 400,
                        isSound: true
                    });
                }
            } else {
                setScanValue("");
                renderGlobalAlert({
                    variant: "exception",
                    title: errorsT("noKizState"),
                    statusCode: 400,
                    isSound: true
                });
            }
        }, Number(process.env.REACT_APP_INTERVAL_REQUEST));
    };

    const { isValid, errors, setErrors } = useForm<IStoreModel & IValidator>({
        validations: {
            supplier: {
                required: {
                    value: data?.idSupplierGlobal ? false : true,
                    message: errorsT("required"),
                },
            },
            store: {
                required: {
                    value: data?.idStoreGlobal ? false : true,
                    message: errorsT("required"),
                },
            },
            pricingModel: {
                required: {
                    value: data?.idPricingModelGlobal ? false : true,
                    message: errorsT("required"),
                },
            },
            documentDate: {
                required: {
                    value: data?.documentDate?.day ? false : true,
                    message: errorsT("required"),
                },
            },
        },
    });

    useEffect(() => {
        const selectedItemDto = selectedItem
            ? data.items.find((x) => x.idGlobal === selectedItem.idGlobal)
            : undefined;

        setSelectedItemCreateDto(selectedItemDto);
        setScanFocus(!!selectedItem);
        setScanValue("");
    }, [selectedItem]);

    useEffect(() => {
        if (data?.items) {
            setAverageMarkup(recalculateAverageMarkup(data.items));
            setSum({
                ...sum,
                amountRetailSum: data.items.reduce((sum, item) => sum + item.retailCostInfo.sum, 0),
                amountRetailVatSum: data.items.reduce((sum, item) => sum + item.retailCostInfo.vatSum, 0),
                amountRetailSumIncVat: data.items.reduce((sum, item) => sum + item.retailCostInfo.sumIncVat, 0),
                amountSupplierSum: data.items.reduce((sum, item) => sum + item.supplierCostInfo.sum, 0),
                amountSupplierVatSum: data.items.reduce((sum, item) => sum + item.supplierCostInfo.vatSum, 0),
                amountSupplierSumIncVat: data.items.reduce((sum, item) => sum + item.supplierCostInfo.sumIncVat, 0),
            });
        }
    }, [data?.items,displayItems]);

    const addItemsFilter = (items = props.document?.items) => {
        let arr: IItemFilter[] = [];
        items?.forEach((x) => {
            arr.push({
                idItem: x.idGlobal as string,
                quantityMax: 9999999, // TODO: magic number fix
                quantityMin: 0,
            });
        });
        setItemsFilter(arr);
    };

    useEffect(() => {
        if ((props.variant === "edit" || props.variant === "copy") && props.document) {
            if (props.variant === "edit")
                lockingDocuments.check(data.idGlobal as string, (e)=> {
                    if (e) setOnlyRead(true);                    
                    else
                        lockingDocuments.send({
                            idTable: idTable,
                            idDocument: data.idGlobal as string,
                        }, (e)=> {
                            setIdLockingDocument(e)
                        });
                })

            addItemsFilter();
            const propsValidateItems = props.document.items.map((el) => ({
                err: false,
                message: "",
                idGlobal: el.idGlobal,
            })) as IValidationItem[];
            setItemsValidation(propsValidateItems);
        }
        sysOptionsDataProvider.getByName(SysOptionName.KizValidationType, (e) => {
            setSysOptionsKizValidationType(e?.value as string);
        });
    }, []);

    useEffect(() => {        
        if (!itemsDocumentByGtin) return
        itemsByGtin.allowKiz(data.items,displayItems,(e) => setKizByGtin(e))
    },[itemsDocumentByGtin])

    useEffect(() => {
        if (props.documentItems) {
            setDisplayItems(
                props.documentItems.map((x): IDisplayItem => {
                    return {
                        idGlobal: x.idGlobal as string,
                        goodsName: x.goods.displayName,
                        scalingRatioName: x.scalingRatio.displayName,
                        quantity: x.quantity,
                        productMargin: x.productMargin,
                        producerPrice: x.producerPrice,
                        goodsCode: x.goodsCode,
                        producer: x.producer,
                        retailPrice: x.retailCostInfo.price,
                        retailPriceIncVat: x.retailCostInfo.priceIncVat,

                        retailAmount: x.retailCostInfo.sum,
                        retailAmountIncVat: x.retailCostInfo.sumIncVat,
                        retailVat: x.retailCostInfo.vat,

                        supplierAmount: x.supplierCostInfo.sum,
                        supplierAmountIncVat: x.supplierCostInfo.sumIncVat,
                        gtin: x.gtin,
                        supplierPrice: x.supplierCostInfo.price,
                        supplierPriceIncVat: x.supplierCostInfo.priceIncVat,
                        supplierVat: x.supplierCostInfo.vat,

                        isKiz: x.isKiz || x.isKizBox,
                        countKizScan: (x.isKiz || x.isKizBox) === true ? x.kizBoxCount + x.kizCount : undefined,
                        isGnvls: x.isGnvls ?? false,
                        denominator: x.denominator,
                        numerator: x.numerator,
                        absentInGC: props.documentUpdate?.idGosContractExternalGlobal ? (x.idGosContractGoodsGlobal ? false : true) : null,
                        idInvoiceItemGroupGlobal: x.idInvoiceItemGroupGlobal,
                        dateCreated: x.dateCreated,
                        bestBefore: x.bestBefore,
                        seriesNumber: x.seriesNumber,
                        isOsu: x.kizOsuDto ? true : x.isOsu,
                        actualQuantity: x.quantity - (x.damageQuantity ?? 0) - (x.defectQuantity ?? 0) - (x.shortageQuantity ?? 0),
                        damageQuantity: x.damageQuantity,
                        defectQuantity: x.defectQuantity,
                        shortageQuantity: x.shortageQuantity
                    };
                })
            );

            const updatedDataItems = data.items.map(item => {
                const documentItem = props.documentItems?.find(docItem => docItem.idGlobal === item.idGlobal);
                if (documentItem) {
                    return {
                        ...item,
                        actualQuantity: documentItem.quantity - (documentItem.damageQuantity ?? 0) - (documentItem.defectQuantity ?? 0) - (documentItem.shortageQuantity ?? 0),
                    };
                }
                return item;
            });
    
            setData({
                ...data,
                items: updatedDataItems
            });
        }
    }, [props.documentItems]);

    const setKizScanComponent = async (gridSelectedItem?: IGridRow, isFocusScanKizBox?: boolean) => {

        // Находим актуальный элемент в data.items по id из gridSelectedItem
        // уменьшает риск гонки за актуальным состоянием, так как нет вторичных зависимостей
        let selectedItem = gridSelectedItem
            ? data.items.find(x => x.idGlobal === gridSelectedItem.idGlobal)
            : selectedItemCreateDto

        if (!selectedItem) {
            setKizScanModal(<></>);
            return;
        }

        try {
            // Запрашиваем ScalingRatio напрямую
            const scalingRatio: IScalingRatioDTO | null = await new Promise<IScalingRatioDTO | null>((resolve) => {

                scalingRatioDP.overrideGetById(
                    selectedItem!.idGoodsGlobal,
                    selectedItem!.idScalingRatioGlobal as string,
                    (result) => resolve(result)
                );
            });

            if (!scalingRatio) {
                setKizScanModal(<></>);
                return;
            }

            setKizScanModal(
                <KizScanModal
                    ok={(value: IInvoiceItemCreateDTO, sr?: IScalingRatioDTO) => {
                        let result = changeItemScalingRatio(value, sr);

                        setData({
                            ...data,
                            items: [...data.items.filter((x) => x.idGlobal !== result.idGlobal), result],
                        });

                        setDisplayItems(
                            displayItems.map((x): IDisplayItem => ({
                                ...x,
                                countKizScan:
                                    x.idGlobal === result.idGlobal
                                        ? sumKizsCount(result, documentType)
                                        : x.countKizScan,
                                scalingRatioName:
                                    x.idGlobal === result.idGlobal
                                        ? sr?.displayName ?? x.scalingRatioName
                                        : x.scalingRatioName,
                                dateCreated:
                                    x.idGlobal === result.idGlobal ? DateTime.now() : x.dateCreated,
                                isOsu: x.idGlobal === result.idGlobal ? value?.isOsu : x.isOsu,
                            }))
                        );

                        setShowKizScan(false);
                        setKizScanModal(<></>);
                    }}
                    cancel={() => {
                        setShowKizScan(false);
                        setKizScanModal(<></>);
                    }}
                    selectedItem={selectedItem}
                    documentType={documentType}
                    sysOptionsKizValidationType={sysOptionsKizValidationType}
                    document={{
                        idTable: idTable,
                        idDocument: data.idGlobal as string,
                    }}
                    idLotFrom={null}
                    scalingRatio={scalingRatio}
                    numerator={scalingRatio.numerator}
                    idGoodsGlobal={selectedItem.idGoodsGlobal}
                    onValidateDuplicate={(barcode) => validateDuplicateKizs(data.items, barcode)}
                    isFocusScanKizBox={isFocusScanKizBox}
                />
            );
        } catch (error) {
            console.error("Ошибка при загрузке scalingRatio:", error);
            setKizScanModal(<></>);
        }
    };

    function setKizByGtin(e: IBaseDocumentItemKizDTO | undefined) {
        const itemId = e?.idGlobal;
        if (!itemId) return;

        const item = data.items.find(x => x.idGlobal === itemId);
        if (!item) return;

        // Обновляем данные элемента
        setData({
            ...data,
            items: [
                ...data.items.filter(x => x.idGlobal !== itemId),
                {
                    ...item,
                    kizs: e.kizs,
                    kizBoxes: e.kizBoxes,
                    kizOsuDto: e.kizOsuDto,
                    kizOsuDtos: e.kizOsuDtos,
                    kizBoxesGisMt: e.kizBoxesGisMt
                }
            ]
        });

        // Обновляем отображаемый элемент
        const displayItem = displayItems.find(x => x.idGlobal === itemId);
        if (displayItem) {
            setDisplayItems([
                ...displayItems.filter(x => x.idGlobal !== itemId),
                {
                    ...displayItem,
                    countKizScan: (displayItem.countKizScan ?? 0) + 1
                }
            ]);
        }

        renderGlobalAlert({
            variant: "success",
            statusCode: 200,
            title: baseT("kizAddedSuccess"),
            isSound: true
        });

        // Если короб, открываем модалку сканирования
        if (itemsDocumentByGtin?.kizParsedDto.barcodeType === BarcodeType.KizBox) {
            setShowKizScan(true);
            setKizScanComponent(selectedItem, true);
        }
    }

    function recalculateAverageMarkup(items: IInvoiceItemCreateDTO[] | undefined): string {// Получение средней наценки.
        let result: number = 0;
        if (items && items.length) {
            for (let i = 0; i < items.length; i++) {
                result += items[i].productMargin;
            }
            result = result / items.length;
        }
        return result > 0 ? result.toFixed(2).toString() : "0";
    }

    const openTab = (entity: IEntitySimpleDTO, gosContractGoods: IGosContractFields) => {
        tabsContext.openChild(
            "invoice_item_creator_plugin",
            "create",
            undefined,
            (value: IInvoiceItemCreateDTO, displayItem: IDisplayItem) => {
                setData({
                    ...data,
                    items: data.items ? [...data.items?.filter((x) => x.idGlobal !== value.idGlobal), value] : [value],
                });
                setDisplayItems([...displayItems.filter((x) => x.idGlobal !== displayItem.idGlobal), displayItem]);

                setItemsFilter([...itemsFilter, { idItem: value.idGlobal, quantityMax: 9999999, quantityMin: 0 }]);
                setItemsValidation([...itemsValidation, { err: false, message: "", idGlobal: value.idGlobal }]);
            },
            {
                goods: entity,
                idPricingModel: data.idPricingModelGlobal,
                idSupplier: data.idSupplierGlobal,
                idStore: data.idStoreGlobal,
                gosContractFields: gosContractGoods,
                currentSupplier: currentSupplier,
                currentPharmacy: currentPharmacy,
                variant: 'create',
                isUnProcessHard: props.document?.isHardUnProcess,
                documentDate: data.documentDate,
                isGiftInvoice: data.isGiftInvoice
            }
        );
    };

    function callOpenInvoiceItemCreatorPlugin(entity: IEntitySimpleDTO) {
        if (data.idGosContractExternalGlobal) {
            gosContractDP.getById(data.idGosContractExternalGlobal, (gosContract) => {

                //Если entity.idGlobal = GosContractGoodsIdGlobal
                let gosContractGoods = gosContract.goods.filter((x) => x.idGlobal === entity.idGlobal);
                //Если entity.idGlobal = IdGoodsGlobal (Goods.idGlobal)
                if (gosContractGoods.length === 0) {
                    gosContractGoods = gosContract.goods.filter((x) => x.goods?.idGlobal === entity.idGlobal);
                }

                if (gosContractGoods.length === 1) {
                    openTab({...entity, idGlobal: gosContractGoods[0].goods.idGlobal}, { 
                        isGosContract: true, 
                        referentPrice: gosContract.priceInfo.referentPrice, 
                        gosContractGoods: gosContractGoods[0] ?? null,
                        idGosContractGlobal: data.idGosContractExternalGlobal!
                     });
                }
                else if (gosContractGoods.length > 1) {
                    setShowMessageModalGosContractGoods({
                        show: true, 
                        entity: entity, 
                        gosContractGoods:gosContractGoods, 
                        idGoodsGlobal: 
                        entity.idGlobal,
                        idGosContractGlobal: data.idGosContractExternalGlobal,
                        referentPrice: gosContract.priceInfo.referentPrice,
                        message: baseT("choiseGosContractGoods")
                    })
                }
            });
        } else {
            openTab(entity, { isGosContract: false, gosContractGoods: null, idGosContractGlobal: null });
        }
    }

    const calcRetailPriceIncVat = (idGlobal: string, value: number, item: IInvoiceItemCreateDTO) => {
        calcInvoiceItem(
            {
                eventTypes: "RetailPriceWithVatChanged",
                idInvoice: "",
                idSupplier: data.idSupplierGlobal,
                idStore: data.idStoreGlobal,
                idItem: "",
                idGoods: item.idGoodsGlobal as string,
                idScalingRatio: item.idScalingRatioGlobal,
                quantity: item.quantity,
                actualQuantity: item.actualQuantity,
                damageQuantity: item.damageQuantity,
                defectQuantity: item.defectQuantity,
                shortageQuantity: item.shortageQuantity,
                producerPrice: item.producerPrice,
                retailCostInfo: { ...item.retailCostInfo, priceIncVat: value as number },
                supplierCostInfo: item.supplierCostInfo,
                registerPrice: item.registerPrice,
            },
            idGlobal
        );
    };

    const calcRetailAmount = (idGlobal: string, value: number, item: IInvoiceItemCreateDTO) => {
        calcInvoiceItem(
            {
                eventTypes: "RetailAmountChanged",
                idInvoice: "",
                idSupplier: data.idSupplierGlobal,
                idStore: data.idStoreGlobal,
                idItem: "",
                idGoods: item.idGoodsGlobal as string,
                idScalingRatio: item.idScalingRatioGlobal,
                quantity: item.quantity,
                actualQuantity: item.actualQuantity,
                damageQuantity: item.damageQuantity,
                defectQuantity: item.defectQuantity,
                shortageQuantity: item.shortageQuantity,
                producerPrice: item.producerPrice,
                retailCostInfo: { ...item.retailCostInfo, sum: value as number },
                supplierCostInfo: item.supplierCostInfo,
                registerPrice: item.registerPrice,
            },
            idGlobal
        );
    };

    function calcInvoiceItem(collectionProps: ICollectPricingInvoiceItemProps, idGlobal: string) {
        if (data.idPricingModelGlobal === undefined || data.idPricingModelGlobal === null || data.idPricingModelGlobal === uuidNull) {
            renderGlobalAlert({ variant: "error", statusCode: 500, title: baseT("messages.pricingModelNotSelected"), instance: baseT("pricingModelService") });
            return;
        }

        let pricingInvoiceDTO = collectPricingInvoiceItemDTO(collectionProps);
        pricingInvoiceDP.getCalculatedInvoiceItemViewAsync(data.idPricingModelGlobal as string, pricingInvoiceDTO, (entity) => {

            if (entity.retailPriceChangeReason) 
                renderGlobalAlert({ variant: "warning", statusCode: 500, title: entity.retailPriceChangeReason, instance: baseT("pricingModelService") });

            let indexItem = data.items.findIndex((item) => item.idGlobal === idGlobal);
            let takeDataObj = data.items.find((item) => item.idGlobal === idGlobal) as IInvoiceItemCreateDTO;

            let indexDisplayItem = displayItems.findIndex((item) => item.idGlobal === idGlobal);
            let takeDisplayObj = displayItems.find((item) => item.idGlobal === idGlobal) as IDisplayItem; 
            
            if (entity.retailPriceChangeReason) {
                setDisplayItems([ ...addValueToArray(displayItems, indexDisplayItem, { ...takeDisplayObj, retailPriceIncVat: 0, }),]);
                renderGlobalAlert({ variant: "warning", statusCode: 500, title: entity.retailPriceChangeReason, instance: baseT("pricingModelService") });
            }

            let newItem: IInvoiceItemCreateDTO = {
                ...takeDataObj,
                retailCostInfo: {
                    vat: entity.percentageOfVatRateRetail,
                    price: entity.retailPrice,
                    vatPrice: entity.retailVatPerUnit,
                    priceIncVat: entity.retailPriceWithVat,
                    sum: entity.retailAmount,
                    vatSum: entity.vatAmountRetail,
                    sumIncVat: entity.retailAmountWithVat,
                    adprice: entity.percentageOfMarkupRetail,
                },
                supplierCostInfo: {
                    vat: entity.supplierVatRatePercentage,
                    price: entity.supplierPriceExcludingVat,
                    vatPrice: entity.supplierVatPerUnit,
                    priceIncVat: entity.supplierPriceWithVat,
                    sum: entity.supplierAmountExcludingVat,
                    vatSum: entity.supplierVatAmount,
                    sumIncVat: entity.supplierAmountWithVat,
                    adprice: entity.supplierMarkupPercentage,
                },
                quantity: entity.quantity,
                idGosContractExternalGlobal: takeDataObj?.idGosContractExternalGlobal,
                actualQuantity: entity.quantity - (takeDataObj.damageQuantity ?? 0) - (takeDataObj.defectQuantity ?? 0) - (takeDataObj.shortageQuantity ?? 0),
                damageQuantity: takeDataObj?.damageQuantity,
                defectQuantity: takeDataObj?.defectQuantity,
                shortageQuantity: takeDataObj?.shortageQuantity,
                registerPrice: entity.registeredPrice,
                producerPrice: entity.manufacturerPrice,
                productMargin: entity.actualMarkupPercentage,
            };

            setData({
                ...data,
                items: addValueToArray(data.items, indexItem, newItem),
            });
            setDisplayItems([
                ...addValueToArray(displayItems, indexDisplayItem, {
                    ...takeDisplayObj,
                    retailVat: entity.percentageOfVatRateRetail,
                    retailPrice: entity.retailPrice,
                    retailPriceIncVat: entity.retailPriceWithVat,
                    retailAmount: entity.retailAmount,
                    retailAmountIncVat: entity.retailAmountWithVat,
                    supplierPrice: entity.supplierPriceExcludingVat,
                    supplierPriceIncVat: entity.supplierPriceWithVat,
                    supplierVat: entity.supplierVatRatePercentage,
                    supplierAmount: entity.supplierAmountExcludingVat, 
                    supplierAmountIncVat: entity.supplierAmountWithVat, 
                    quantity: entity.quantity,
                    actualQuantity: entity.quantity - (takeDisplayObj.damageQuantity ?? 0) - (takeDisplayObj.defectQuantity ?? 0) - (takeDisplayObj.shortageQuantity ?? 0),
                    damageQuantity: takeDisplayObj?.damageQuantity,
                    defectQuantity: takeDisplayObj?.defectQuantity,
                    shortageQuantity: takeDisplayObj?.shortageQuantity,
                    productMargin: entity.percentageOfMarkupRetail
                }),
            ]);
        });
    }

    function changeItemScalingRatio(value: IInvoiceItemCreateDTO, scalingRatio?: IScalingRatioDTO) {
        if (!scalingRatio) return value;
        return {
            ...value,
            idScalingRatioGlobal: scalingRatio.idGlobal,
            numerator: scalingRatio.numerator,
            dateCreated: DateTime.now()
        } as IInvoiceItemCreateDTO;
    }

    const isValidDocument = (items: IInvoiceItemCreateDTO[], showMessage: () => void): boolean => {
        var flag = true;
        if (!items || items.length === 0) {
            flag = false;
            showMessage();
        }
        return flag;
    };

    function calcAllItems()
    {
        if (!data.items || !displayItems || displayItems.length === 0) return;
        data.items.forEach((x)=>{
            calcRow({propertyName: "quantity", value: x.quantity, idGlobal: x.idGlobal});
        });       
    }

    function getItemsByGtin(barcode: string)
    {   
        itemsByGtin.setKizIdItemsByGtin(undefined)
        itemsByGtin.getItems(barcode, data.idGlobal as string)
    }

    function validateProducerPriceVsRegisterPrice(item: IInvoiceItemCreateDTO | undefined ) {
        const errors: IErrors[] = [];

        if (!item) {
            return errors;
        }
        const itemsArray = Array.isArray(item) ? item : [item];
        
        itemsArray.forEach((item) => {
            const producerPrice = item.producerPrice || 0;
            const registerPrice = item.registerPrice || 0;
            const isGnvls = item?.isGnvls;
            const itemFromDisplayItem = displayItems.find((displayItem) => displayItem.idGlobal === item.idGlobal);
            const itemName = itemFromDisplayItem?.goodsName
            
            if (registerPrice > 0 && producerPrice > registerPrice && isGnvls) {
                errors.push({
                    itemName: itemName,
                    producerPrice: producerPrice,
                    registerPrice: registerPrice,
                    message: `${t('documents.invoice.messages.producerPriceErrorPart1')} "${itemName}" ${t('documents.invoice.messages.producerPriceErrorPart2')} ${producerPrice} ${t('documents.invoice.messages.producerPriceErrorPart3')} ${registerPrice}`
                });
                errors.forEach(error => {
                    renderGlobalAlert({ variant: "warning", statusCode: 0, title: t('documents.invoice.messages.producerPriceTitle'), detail: error.message }); 
                });
            };
        });
        return errors;
    }

    const validateBeforeProcess = (row:IChangeDataGrid) => {
        const currentItem = data?.items.find((item) => item.idGlobal === row.idGlobal);
        const errors = validateProducerPriceVsRegisterPrice(currentItem);
        return errors.length === 0;
    };

    const calcRow =  (row:IChangeDataGrid) => { 
        const item = data.items.find((item) => item.idGlobal === row.idGlobal);
        if (!item) {
            return;
        }

        if (row.propertyName === "quantity") {
            calcInvoiceItem(
                {
                    eventTypes: "QuantityСhanged",
                    idInvoice: "",
                    idSupplier: data.idSupplierGlobal,
                    idStore: data.idStoreGlobal,
                    idItem: "",
                    idGoods: item.idGoodsGlobal as string,
                    idScalingRatio: item.idScalingRatioGlobal,
                    quantity: row.value as number,
                    actualQuantity: item.actualQuantity,
                    damageQuantity: item.damageQuantity,
                    defectQuantity: item.defectQuantity,
                    shortageQuantity: item.shortageQuantity,
                    producerPrice: item.producerPrice,
                    retailCostInfo: { ...item.retailCostInfo, adprice: 0 as number },
                    supplierCostInfo: item.supplierCostInfo,
                    registerPrice: item.registerPrice,
                },
                row.idGlobal
            );
            return;
        }

        switch (row.propertyName) {
            case "retailAmount":
                calcRetailAmount(row.idGlobal, row.value as number, item);
                break;
                case "retailPriceIncVat":
                validateBeforeProcess(row)
                calcRetailPriceIncVat(row.idGlobal, row.value as number, item);
                break;
        }
    }

    const handleSave = (dataToSave: IInvoiceCreateDTO) => {
        props.save(dataToSave);
    };

    const handleOk = (dataToProcess: IInvoiceCreateDTO) => {
        props.ok(dataToProcess);
    };

    const onOkAction = () => {
        lockingDocuments.delete({
            idTable: idTable,
            idDocument: data.idGlobal as string,
        });
		saveDocument(
            (dataToProcess) => handleOk(dataToProcess),
            () => kizCounter(data.items, () => setShowMessageModal({ 
                message: errorsT("kizCountNotMatch"), 
                show: true, 
                btSaveOrOk: "ok" 
            }), documentType, sysOptionsKizValidationType)
        );
    };

    const onSaveAction = () => {
        saveDocument(
            (dataToSave) => handleSave(dataToSave),
            () => kizCounter(data.items, () => setShowMessageModal({ 
                message: errorsT("kizCountNotMatch"), 
                show: true, 
                btSaveOrOk: "save" 
            }), documentType, sysOptionsKizValidationType)
        );
    };

    const onCancel = () => {
        lockingDocuments.delete({
            idTable: idTable,
            idDocument: data.idGlobal as string,
        });

        props.cancel();
    };

    const saveDocument = (callback: (data: IInvoiceCreateDTO) => void, kizCounter: () => boolean) => {
        if (
            isValid() &&
            isValidDocument(data.items, () => setShowMessageModalValidDocument({ message: errorsT("validDocument"), show: true })) === true &&
            kizCounter() === true
        ) {
            callback(data);
        }
    }

    useAutoSaveDocument(data.items, () => saveDocument(handleSave, () => {return true}))

    return (
        <>
            <BaseDocumentCreator
                valid={true}
                ok={{
                    onClick: onOkAction,
                    disabled: onlyRead || itemsValidation.some(x => x.err) || props.isSubmitting,
                    sendRequestGlobal: props.isSubmitting,
                }}
                save={{
                    onClick: onSaveAction,
                    disabled: onlyRead || itemsValidation.some(x => x.err) || props.isSubmitting,
                    sendRequestGlobal: props.isSubmitting,
                }}
                cancel={{ onClick: onCancel }}
                print={{
                    onClick: () => {
                        return idTable;
                    },
                    onSubItemClick: () => {
                        let arr = new Array<IKeyValuePair>();
                        if (props.document != undefined && props.document.mnemocode != undefined && props.idGlobal != undefined) arr.push({ key: props.idGlobal, value: props.document.mnemocode });
                        return arr;
                    },
                }}
            >
                <div className={styles.glwrapper}>
                    <div className={styles.wrapper}>
                        <div>
                            {isCompact ? (
                                <>
                                    <GridWrapper cols={35}>
                                        <FlexRow className={styles.compactFieldFields} wrap>
                                            <div>
                                                <Button 
                                                    disabled={!data.items || data.items.length === 0 || onlyRead || disabledGosContract}
                                                    className={styles.compactField_Recalculate}
                                                    variant="primary"
                                                    onClick={() => {
                                                        calcAllItems();
                                                    }} 
                                                >
                                                    Пересчитать
                                                </Button>
                                            </div>
                                            <GridSelectorInput
                                                required
                                                disabled={onlyRead || props.document?.isHardUnProcess}
                                                variant="required"
                                                selectorModalJsx={ContractorSelectorModal}
                                                id={"dictionaryInputInvoiceSupplier"}
                                                label={t("documents.invoice.supplier")}
                                                className={styles.compactFieldContractor}
                                                selectedEntity={supplier}
                                                onSelect={(value) => setSupplier({ ...value })}
                                                onClear={() => setSupplier(null)}
                                                error={errors.supplier}
                                                onFocus={() => setErrors({ ...errors, supplier: undefined })}
                                                treeViewCheckDirectoryType='supplier'
                                                applyUserFilter={true}
                                            />
                                            <TextInput
                                                label={t("documents.invoice.number")}
                                                inputId={"inputInvoiceNumber"}
                                                value={mnemocode}
                                                disabled
                                            />
                                            <DateInput
                                                required
                                                disabled={onlyRead}
                                                labelVariant="required"
                                                inputId={"inputInvoiceDate"}
                                                label={t("documents.invoice.date")}
                                                value={data.documentDate}
                                                error={errors.documentDate}
                                                onBlur={(value) => {
                                                    setData({ ...data, documentDate: value as DateTime });
                                                }}
                                            />
                                            <StoreSelectorPanel                                            
                                                we
                                                required
                                                compactMode
                                                idTable={idTable}
                                                contractor={{ 
                                                     label: t("documents.invoice.pharmacy"),
                                                     disabled: onlyRead || props.document?.isHardUnProcess,
                                                     treeViewCheckDirectoryType:'pharmacy'
                                                }}
                                                store={{
                                                    className: styles.gridSelInputCustom, 
                                                    label: t("documents.invoice.store"),
                                                    value: store,
                                                    onSelect: (value) => setStore(value),
                                                    onClear: () => setStore(null),
                                                    error: errors.store,
                                                    onFocus: () => setErrors({ ...errors, store: undefined }),
                                                    disabled: onlyRead || props.document?.isHardUnProcess
                                                }}
                                                pricingModel={{
                                                    label: t("documents.invoice.pricingModel"),
                                                    onSelect: (value) => setPricingModel(value),
                                                }}
                                                notAutoComplete={isCompactStore === true || props.variant === "edit" || props.variant === "copy"}
                                                documentStore={store ?? undefined}
                                                documentPricingModel={pricingModel ?? undefined}
                                            />
                                            <GridSelectorInput
                                                required
                                                disabled={onlyRead}
                                                variant="required"
                                                selectorModalJsx={PricingModelSelectorModal}
                                                selectedEntity={pricingModel}
                                                label={t("documents.invoice.pricingModel")}
                                                id={"dictionaryInputPricingModel"}
                                                onSelect={(entity) => setPricingModel({ ...entity })}
                                                onClear={() => setPricingModel(null)}
                                                error={errors.pricingModel}
                                                onFocus={() => {
                                                    setErrors({ ...errors, pricingModel: undefined });
                                                }}
                                            />
                                            <CheckBox
                                                id="isAgentContract"
                                                label={baseT("agentContract")}
                                                disabled={onlyRead}
                                                defaultChecked={data.isAgentContract ?? props.document?.isAgentContract ?? false}
                                                onChanged={(checked) => setData({ ...data, isAgentContract: checked })}
                                            />
                                            <CheckBox
                                                id="isGiftInvoice"
                                                label={baseT("isGiftInvoice")}
                                                disabled={onlyRead}
                                                defaultChecked={data.isGiftInvoice ?? props.document?.isGiftInvoice ?? false}
                                                onChanged={(checked) => setData({ ...data, isGiftInvoice: checked })}
                                            />
                                        </FlexRow>
                                        <div className={styles.compactFieldCompactMode}>
                                            <Tooltip title={baseT("basicModeUi")}>
                                                <StockBalanceButtonExpand
                                                    onClick={() => {
                                                        setIsCompact(!isCompact);
                                                    }}
                                                    sizeVariant="normal"
                                                    colorVariant="default"
                                                />
                                            </Tooltip>
                                        </div>
                                    </GridWrapper>
                                    <Accordion bold opened={false} caption="" id={uuidv4()}>
                                        <Accordion opened={false} caption={t("documents.invoice.addition")} id={`additionalInvoiceInfo-${uuidv4()}`}>
                                            <GridWrapper cols={2}>
                                                <GridSelectorInput
                                                    selectorModalJsx={GosContractSelectorModal}
                                                    selectedEntity={gosContract}
                                                    disabled={onlyRead || data.items?.some(item => !!item.idGosContractExternalGlobal)}
                                                    onSelect={(value: IEntitySimpleDTO) => {
                                                        setGosContract(value);
                                                        setData({ ...data, idGosContractExternalGlobal: value.idGlobal });
                                                    }}
                                                    label={t("documents.invoice.stateContract")}
                                                    onClear={() => {
                                                        setGosContract(null);
                                                        setData({ ...data, idGosContractExternalGlobal: undefined });
                                                    }}
                                                    inline={true}
                                                />
                                                <DictionaryInput 
                                                    disabled 
                                                    label={t("documents.invoice.contract")} 
                                                    inputId={"dictionaryInputContract"} 
                                                    inline={true} 
                                                />
                                            </GridWrapper>
                                        </Accordion>
                                        <Accordion opened={false} caption={t("documents.invoice.accompanyingDocuments")} id={`accompanyingInvoiceInfo-${uuidv4()}`}>
                                            <FlexRow wrap>
                                                <TextInput
                                                    disabled={onlyRead ? true : false}
                                                    label={t("documents.invoice.invoice")}
                                                    inputId={"inputAccompanyingInvoiceNumber"}
                                                    value={data?.supplierDocNumber}
                                                    onChange={(value) => setData({ ...data, supplierDocNumber: value })}
                                                />
                                                <DateInput
                                                    disabled={onlyRead ? true : false}
                                                    className={styles.field_InvoiceDate}
                                                    label={t("documents.invoice.date")}
                                                    inputId={"inputAccompanyingInvoiceDate"}
                                                    value={data?.supplierDocDate}
                                                    onBlur={(value) => value && setData({ ...data, supplierDocDate: value })}
                                                />
                                                <DateInput
                                                    disabled={onlyRead ? true : false}
                                                    className={styles.field_InvoicePaymentDate}
                                                    label={t("documents.invoice.dateOfPayment")}
                                                    inputId={"inputAccompanyingInvoicePaymentDate"}
                                                    value={data?.datePay}
                                                    onBlur={(value) => value && setData({ ...data, datePay: value })}
                                                />
                                                <DateInput
                                                    className={styles.field_InvoiceActualPaymentDate}
                                                    disabled
                                                    label={t("documents.invoice.inputAccompanyingInvoiceActualPaymentDate")}
                                                    inputId={"inputAccompanyingInvoiceActualPaymentDate"}
                                                />
                                                <TextInput
                                                    disabled={onlyRead ? true : false}
                                                    label={t("documents.invoice.check")}
                                                    inputId={"inputAccompanyingBillNumber"}
                                                    value={data?.supplierBillDocNumber}
                                                    onChange={(value) => {
                                                        setData({ ...data, supplierBillDocNumber: value });
                                                    }}
                                                />
                                                <DateInput
                                                    disabled={onlyRead ? true : false}
                                                    className={styles.field_BillDate}
                                                    label={t("documents.invoice.date")}
                                                    inputId={"inputAccompanyingBillDate"}
                                                    value={data?.supplierBillDocDate}
                                                    onBlur={(value) => {
                                                        value && setData({ ...data, supplierBillDocDate: value });
                                                    }}
                                                />
                                                <TextInput
                                                    className={styles.field_TargetProgram}
                                                    disabled
                                                    label={t("documents.invoice.targetProgram")}
                                                    inputId={"inputAccompanyingTargetProgram"}
                                                    /*value={accompanyingInvoiceInfo?.targetProgram} onChange={(value) => setAccompanyingInvoiceInfo({ ...accompanyingInvoiceInfo, targetProgram: value })}*/
                                                />
                                            </FlexRow>
                                        </Accordion>
                                    </Accordion>
                                </>
                            ) : (
                                <>
                                    <GridWrapper cols={4} baseline>
                                        <GridSelectorInput
                                            required
                                            disabled={onlyRead|| props.document?.isHardUnProcess}
                                            variant="required"
                                            selectorModalJsx={ContractorSelectorModal}
                                            className={styles.field_Provider}
                                            inline
                                            id={"dictionaryInputInvoiceSupplier"}
                                            label={t("documents.invoice.supplier")}
                                            selectedEntity={supplier}
                                            onSelect={(value) => setSupplier({ ...value })}
                                            onClear={() => setSupplier(null)}
                                            error={errors.supplier}
                                            onFocus={() => setErrors({ ...errors, supplier: undefined })}
                                            treeViewCheckDirectoryType="supplier"
                                            applyUserFilter={true}
                                        />
                                        <TextInput
                                            className={styles.field_Number}
                                            disabled
                                            label={t("documents.invoice.number")}
                                            inputId={"inputInvoiceNumber"}
                                            inline={true}
                                            value={mnemocode}
                                        />
                                        <DateInput
                                            required
                                            disabled={onlyRead}
                                            labelVariant="required"
                                            inputId={"inputInvoiceDate"}
                                            className={styles.field_Date}
                                            label={t("documents.invoice.date")}
                                            inline
                                            value={data.documentDate}
                                            error={errors.documentDate}
                                            onBlur={(value) => {
                                                setData({ ...data, documentDate: value as DateTime });
                                            }}
                                        />
                                        <div className={styles.field_CompactMode}>
                                            <Tooltip title={baseT("compactModeUi")}>
                                                <StockBalanceButtonCompress
                                                    onClick={() => {
                                                        setIsCompact(!isCompact);
                                                        setIsCompactStore(true);
                                                    }}
                                                    sizeVariant="normal"
                                                    colorVariant="default"
                                                />
                                            </Tooltip>
                                        </div>
                                    </GridWrapper>
                                    <div>
                                        <GridWrapper cols={4} baseline>
                                            <StoreSelectorPanel
                                                we
                                                required
                                                idTable={idTable}
                                                contractor={{ 
                                                    label: t("documents.invoice.pharmacy"),
                                                    disabled: onlyRead ? true : false || props.document?.isHardUnProcess,
                                                    treeViewCheckDirectoryType:'pharmacy'
                                                }}
                                                store={{
                                                    className: styles.gridSelInputCustom, 
                                                    label: t("documents.invoice.store"),
                                                    value: store,
                                                    onSelect: (value) => setStore(value),
                                                    onClear: () => setStore(null),
                                                    error: errors.store,
                                                    onFocus: () => setErrors({ ...errors, store: undefined }),
                                                    disabled: onlyRead ? true : false || props.document?.isHardUnProcess
                                                }}
                                                pricingModel={{
                                                    label: t("documents.invoice.pricingModel"),
                                                    onSelect: (value) => setPricingModel(value),
                                                }}
                                                notAutoComplete={isCompactStore === true || props.variant === "edit" || props.variant === "copy"}
                                                documentStore={store ?? undefined}
                                                documentPricingModel={pricingModel ?? undefined}
                                            />
                                            <CheckBox
                                                id="isAgentContract"
                                                label="Агентский контракт"
                                                disabled={onlyRead}
                                                defaultChecked={data.isAgentContract ?? props.document?.isAgentContract ?? false}
                                                onChanged={(checked) => setData({ ...data, isAgentContract: checked })}
                                            />
                                            <CheckBox
                                                id="isGiftInvoice"
                                                label={baseT("isGiftInvoice")}
                                                disabled={onlyRead}
                                                defaultChecked={data.isGiftInvoice ?? props.document?.isGiftInvoice ?? false}
                                                onChanged={(checked) => setData({ ...data, isGiftInvoice: checked })}
                                            />
                                        </GridWrapper>
                                    </div>
                                    <GridWrapper cols={6}>
                                        <GridSelectorInput
                                            required
                                            variant="required"
                                            disabled={onlyRead}
                                            className={styles.field_PriceModel}
                                            selectorModalJsx={PricingModelSelectorModal}
                                            label={t("documents.invoice.pricingModel")}
                                            id={"dictionaryInputPricingModel"}
                                            inline
                                            selectedEntity={pricingModel}
                                            onSelect={(entity) => setPricingModel({ ...entity })}
                                            error={errors.pricingModel}
                                            onClear={() => setPricingModel(null)}
                                            onFocus={() => {
                                                setErrors({ ...errors, pricingModel: undefined });
                                            }}
                                        />
                                        <Button 
                                            disabled={data.items && data?.items?.length > 0 && !onlyRead && !disabledGosContract? false : true}
                                            className={styles.field_Recalculate} variant="primary"
                                            onClick={() => {
                                                calcAllItems();
                                            }} 
                                        >
                                            Пересчитать
                                        </Button>
                                    </GridWrapper>

                                    <Accordion opened={false} caption={t("documents.invoice.addition")} id={`additionalInvoiceInfo-${uuidv4()}`}>
                                        <GridWrapper cols={2}>
                                            <GridSelectorInput
                                                selectorModalJsx={GosContractSearchSelectorModal}
                                                selectedEntity={gosContract}
                                                disabled={onlyRead || data.items?.some(item => !!item.idGosContractExternalGlobal)}
                                                onSelect={(value: IEntitySimpleDTO) => {
                                                    setGosContract(value);
                                                    setData({ ...data, idGosContractExternalGlobal: value.idGlobal });
                                                }}
                                                label={t("documents.invoice.stateContract")}
                                                onClear={() => {
                                                    setGosContract(null);
                                                    setData({ ...data, idGosContractExternalGlobal: undefined });
                                                }}
                                                inline
                                            />
                                            <DictionaryInput disabled label={t("documents.invoice.contract")} inputId={"dictionaryInputContract"} inline={true} />
                                        </GridWrapper>
                                    </Accordion>
                                    <Accordion opened={false} caption={t("documents.invoice.accompanyingDocuments")} id={`accompanyingInvoiceInfo-${uuidv4()}`}>
                                        <GridWrapper cols={4}>
                                            <TextInput
                                                disabled={onlyRead}
                                                className={styles.field_InvoiceNumber}
                                                labelClassName={styles.invoiceNumberLabel}
                                                label={t("documents.invoice.invoice")}
                                                inputId={"inputAccompanyingInvoiceNumber"}
                                                inline
                                                value={data?.supplierDocNumber}
                                                onChange={(value) => setData({ ...data, supplierDocNumber: value })}
                                            />
                                            <DateInput
                                                disabled={onlyRead}
                                                className={styles.field_InvoiceDate}
                                                label={t("documents.invoice.date")}
                                                inputId={"inputAccompanyingInvoiceDate"}
                                                inline
                                                value={data?.supplierDocDate}
                                                onBlur={(value) => value && setData({ ...data, supplierDocDate: value })}
                                            />
                                            <DateInput
                                                disabled={onlyRead}
                                                className={styles.field_InvoicePaymentDate}
                                                label={t("documents.invoice.dateOfPayment")}
                                                inputId={"inputAccompanyingInvoicePaymentDate"}
                                                inline
                                                value={data?.datePay}
                                                onBlur={(value) => value && setData({ ...data, datePay: value })}
                                            />
                                            <DateInput
                                                className={styles.field_InvoiceActualPaymentDate}
                                                disabled
                                                label={t("documents.invoice.inputAccompanyingInvoiceActualPaymentDate")}
                                                inputId={"inputAccompanyingInvoiceActualPaymentDate"}
                                                inline
                                            />
                                        </GridWrapper>

                                        <GridWrapper cols={4}>
                                            <TextInput
                                                disabled={onlyRead}
                                                className={styles.field_BillNumber}
                                                label={t("documents.invoice.check")}
                                                inputId={"inputAccompanyingBillNumber"}
                                                inline
                                                value={data?.supplierBillDocNumber}
                                                onChange={(value) => {
                                                    setData({ ...data, supplierBillDocNumber: value });
                                                }}
                                            />
                                            <DateInput
                                                disabled={onlyRead}
                                                className={styles.field_BillDate}
                                                label={t("documents.invoice.date")}
                                                inputId={"inputAccompanyingBillDate"}
                                                inline
                                                value={data?.supplierBillDocDate}
                                                onBlur={(value) => {
                                                    value && setData({ ...data, supplierBillDocDate: value });
                                                }}
                                            />

                                            <TextInput
                                                className={styles.field_TargetProgram}
                                                disabled
                                                label={t("documents.invoice.targetProgram")}
                                                inputId={"inputAccompanyingTargetProgram"}
                                                inline
                                            />
                                        </GridWrapper>
                                    </Accordion>
                                </>
                            )}
                            <FlexRow className={styles.field_flexbox}>
                                <PositionEditPanel
                                    kiz={{
                                        onClick: () => {
                                            itemsByGtin.setKizIdItemsByGtin(undefined)
                                            setShowKizScan(true)
                                            setKizScanComponent(selectedItemRef.current)
                                        },
                                        disabled:
                                            (((selectedItemCreateDto?.isKiz || selectedItemCreateDto?.isKizBox) && !onlyRead)
                                                ? false
                                                : true) || props.document?.isHardUnProcess,
                                    }}
                                    edit={{
                                        onClick: async () => {
                                            if (!selectedItem) return;

                                            // Получаем актуальный элемент из данных
                                            const itemDto = data.items.find((x) => x.idGlobal === selectedItem.idGlobal);
                                            if (!itemDto) return;

                                            try {
                                                // Запрашиваем scalingRatio напрямую
                                                const scalingRatio = await new Promise<IScalingRatioDTO | null>((resolve) => {
                                                    scalingRatioDP.overrideGetById(
                                                        itemDto.idGoodsGlobal,
                                                        itemDto.idScalingRatioGlobal as string,
                                                        (result) => resolve(result)
                                                    );
                                                });

                                                // Запрашиваем goods напрямую
                                                const goods = await new Promise<IGoodsDTO | null>((resolve) => {
                                                    goodsDP.getById(itemDto.idGoodsGlobal, (result) => resolve(result));
                                                });

                                                // Проверяем, что данные получены
                                                if (!scalingRatio || !goods) {
                                                    console.error("Не удалось загрузить данные для редактирования");
                                                    return;
                                                }

                                                // Открываем модальное окно с актуальными данными
                                                if (data.idGosContractExternalGlobal) {
                                                    gosContractDP.getById(data.idGosContractExternalGlobal, (gosContract: IGosContractDTO) => {
                                                        const gosContractGoods = gosContract.goods.find(
                                                            (x) => x.goods.idGlobal === goods.idGlobal
                                                        );

                                                        tabsContext.openChild(
                                                            "invoice_item_creator_plugin",
                                                            "edit",
                                                            undefined,
                                                            (value: IInvoiceItemCreateDTO, displayItem: IDisplayItem) => {
                                                                setData({
                                                                    ...data,
                                                                    items: [...data.items.filter((x) => x.idGlobal !== value.idGlobal), value],
                                                                });
                                                                setDisplayItems([...displayItems.filter((x) => x.idGlobal !== displayItem.idGlobal), displayItem]);
                                                            },
                                                            {
                                                                item: itemDto,
                                                                goods: {
                                                                    displayName: goods.goodsName.name as string,
                                                                    idGlobal: goods.idGlobal as string,
                                                                } as IEntitySimpleDTO,
                                                                displayItem: displayItems.find((x) => x.idGlobal === selectedItem.idGlobal),
                                                                idPricingModel: data.idPricingModelGlobal,
                                                                idSupplier: data.idSupplierGlobal,
                                                                idStore: data.idStoreGlobal,
                                                                scalingRatio: {
                                                                    displayName: scalingRatio.displayName,
                                                                    idGlobal: scalingRatio.idGlobal
                                                                } as IEntitySimpleDTO,
                                                                gosContractFields: {
                                                                    isGosContract: true,
                                                                    gosContractGoods: gosContractGoods ?? null,
                                                                    idGosContractGlobal: data.idGosContractExternalGlobal
                                                                } as IGosContractFields,
                                                                currentSupplier: currentSupplier,
                                                                currentPharmacy: currentPharmacy,
                                                                variant: 'edit',
                                                                onlyRead: onlyRead,
                                                                isUnProcessHard: props.document?.isHardUnProcess,
                                                                documentDate: data.documentDate,
                                                                invoiceItemsGroup: data.items.filter((element) =>
                                                                    element.idInvoiceItemGroupGlobal === itemDto.idInvoiceItemGroupGlobal
                                                                ),
                                                                isGiftInvoice: data.isGiftInvoice
                                                            }
                                                        );
                                                    });
                                                } else {
                                                    tabsContext.openChild(
                                                        "invoice_item_creator_plugin",
                                                        "edit",
                                                        undefined,
                                                        (value: IInvoiceItemCreateDTO, displayItem: IDisplayItem) => {
                                                            setData({
                                                                ...data,
                                                                items: [...data.items.filter((x) => x.idGlobal !== value.idGlobal), value],
                                                            });
                                                            setDisplayItems([...displayItems.filter((x) => x.idGlobal !== displayItem.idGlobal), displayItem]);
                                                        },
                                                        {
                                                            item: itemDto,
                                                            goods: {
                                                                displayName: goods.goodsName.name as string,
                                                                idGlobal: goods.idGlobal as string,
                                                            } as IEntitySimpleDTO,
                                                            scalingRatio: {
                                                                displayName: scalingRatio.displayName,
                                                                idGlobal: scalingRatio.idGlobal
                                                            } as IEntitySimpleDTO,
                                                            displayItem: displayItems.find((x) => x.idGlobal === selectedItem.idGlobal),
                                                            idPricingModel: data.idPricingModelGlobal,
                                                            idSupplier: data.idSupplierGlobal,
                                                            idStore: data.idStoreGlobal,
                                                            gosContractFields: {
                                                                isGosContract: false,
                                                                gosContractGoods: null,
                                                            },
                                                            currentSupplier: currentSupplier,
                                                            currentPharmacy: currentPharmacy,
                                                            variant: 'edit',
                                                            isUnProcessHard: props.document?.isHardUnProcess,
                                                            onlyRead: onlyRead,
                                                            documentDate: data.documentDate,
                                                            invoiceItemsGroup: data.items.filter((element) =>
                                                                element.idInvoiceItemGroupGlobal === itemDto.idInvoiceItemGroupGlobal
                                                            ),
                                                            isGiftInvoice: data.isGiftInvoice
                                                        }
                                                    );
                                                }
                                            } catch (error) {
                                                console.error("Ошибка при загрузке данных для редактирования:", error);
                                            }
                                        },
                                        disabled: selectedItem ? false : true,
                                    }}
                                    delete={{
                                        onClick: () => {
                                            if (!selectedItem) return;

                                            const itemId = selectedItem.idGlobal;

                                            // Удаляем элемент из основных данных
                                            setData({
                                                ...data,
                                                items: data.items.filter((x) => x.idGlobal !== itemId),
                                            });

                                            // Удаляем из отображаемых элементов
                                            setDisplayItems(displayItems.filter((x) => x.idGlobal !== itemId));

                                            // Очищаем выбор
                                            setSelectedItem(undefined);
                                            selectedItemRef.current = undefined

                                            // Удаляем из валидаций
                                            const itemsValid = itemsValidation.filter((item) => item.idGlobal !== itemId);
                                            const itemsFilterValid = itemsFilter.filter((item) => item.idItem !== itemId);
                                            setItemsValidation(itemsValid);
                                            setItemsFilter(itemsFilterValid);
                                        },
                                        disabled: !selectedItem || onlyRead || props.document?.isHardUnProcess
                                    }}
                                    copy={{
                                        onClick: () => {
                                            if (!selectedItem) return;
                                            
                                            const itemId = selectedItem.idGlobal;
                                            
                                            const itemToCopy = data.items.find((x) => x.idGlobal === itemId);
                                            const displayItemToCopy = displayItems.find((x) => x.idGlobal === itemId)
                                            const idGlobalItemCopy = uuidv4()

                                            if (!itemToCopy) return;
                                            const copiedItem: IInvoiceItemCreateDTO = {
                                                ...itemToCopy,
                                                idGlobal: idGlobalItemCopy,
                                                kizCount: 0,
                                                kizBoxCount: 0,
                                                kizs: [],
                                                kizOsuDtos: [],
                                                kizBoxes: [],
                                                kizBoxesGisMt: []
                                            };

                                            setData({
                                                ...data,
                                                items: [...data.items, copiedItem]
                                            });

                                            if (!displayItemToCopy) return;
                                                                                        
                                            const copiedDisplayItem: IDisplayItem = {
                                                ...displayItemToCopy,
                                                idGlobal: idGlobalItemCopy,
                                                countKizScan: 0
                                            };                                

                                            setDisplayItems([...displayItems, copiedDisplayItem]);

                                            const itemValidationsToCopy = itemsValidation.filter((item) => item.idGlobal === itemId); //
                                            
                                            if (itemValidationsToCopy.length > 0) {
                                                const copiedValidations = itemValidationsToCopy.map(validation => ({
                                                    ...validation,
                                                    idGlobal: idGlobalItemCopy
                                                }));
                                                
                                                setItemsValidation([...itemsValidation, ...copiedValidations]);
                                            }

                                            const itemFiltersToCopy = itemsFilter.filter((item) => item.idItem === itemId);
                                            
                                            if (itemFiltersToCopy.length > 0) {
                                                const copiedFilters = itemFiltersToCopy.map(filter => ({
                                                    ...filter,
                                                    idItem: idGlobalItemCopy
                                                }));
                                                
                                                setItemsFilter([...itemsFilter, ...copiedFilters]);
                                            }
                                        },
                                        disabled: !selectedItem || onlyRead || props.document?.isHardUnProcess
                                    }}
                                />
                                <GridSelectorInput
                                    barcodeScan={tabsContext.currentTab && tabsContext.currentTab?.id === currentIdTab.current}
                                    disabled={onlyRead ? true : false || props.document?.isHardUnProcess}
                                    selectorModalJsx={GoodsSelectorModal}
                                    gosContract={data?.idGosContractExternalGlobal}
                                    searchOption={searchOption.value}
                                    className={styles.field_SelectionGoods}
                                    error={errorSelectGoods}
                                    onKeyDown={(e) => {
                                        checkCapsLock(e, (isCapsLock)=> {
                                             if (isCapsLock) {
                                                setErrorSelectGoods(t("documents.invoice.capsLockOn"));
                                                return
                                            }
                                            else {
                                                setErrorSelectGoods(undefined);
                                            }
                                        })
                                    }}
                                    id={"inputAccompanyingSelectionGoods"}
                                    inline={true}
                                    label={t("documents.invoice.selectionOfGoods")}
                                    valid={() => isValid()}
                                    onSelect={(entity,searchQuery) => {
                                        if (searchOption.value === SearchOption.barcode || searchOption.value === SearchOption.goodsCode) {
                                            if (entity.length > 1) {
                                                setGoodsViewItem({ item: entity, show: true })
                                            }
                                            else {
                                                callOpenInvoiceItemCreatorPlugin(entity);
                                            }
                                                
                                        } 
                                        else if (searchOption.value === SearchOption.gtin && searchQuery) {
                                            getItemsByGtin(searchQuery as string)
                                        } else {
                                            callOpenInvoiceItemCreatorPlugin(entity);
                                        }
                                    }}
                                    lastSearchQuerySettings = {lastSearchQuerySettings}
                                    autoFocus={showLastSearchQuery}
                                    key={selectedItem?.idGlobal}
                                />
                                <Select
                                    disabled={onlyRead}
                                    barcodeScan={tabsContext.currentTab && tabsContext.currentTab?.id === currentIdTab.current}
                                    value={searchOption}
                                    onSelect={(option) => {
                                        setSearchOption(option);
                                    }}
                                    className={styles.field_SelectBlock}
                                    options={[
                                        { displayName: t("documents.invoice.barcode"), value: "barcode" },
                                        { displayName: t("documents.invoice.name"), value: "name" },
                                        { displayName: t("documents.invoice.goodsCode"), value: "goodsCode" },
                                        { displayName: t("documents.invoice.gtin"), value: SearchOption.gtin, hidden: props.variant !== "edit"} 
                                    ]}
                                    defaultOption={defaultSearchOption}
                                />
                                <div className={styles.SettingsButton}>
                                    <TabIndexSettingsButtonModal 
                                        showModal={showSettings}
                                        modalWindow={SettingsModal}
                                    />
                                </div>
                            </FlexRow>
                            <TextInput
                                disabled={onlyRead}
                                className={styles.scanField}
                                autoFocus={scanFocus}
                                value={scanValue}
                                onChange={(e) => {
									let isKiz = data.items.find(c => c.idGlobal === selectedItem?.idGlobal)?.isKiz
                                    let isKizBox = data.items.find(c => c.idGlobal === selectedItem?.idGlobal)?.isKizBox
                                    if (isKiz || isKizBox) {
                                        addKiz(e);
                                        setScanValue(e);
                                    }
                                }}
                            />
                            <ChangeableGrid
                                documentStateProc={onlyRead}
                                itemsFilter={itemsFilter}
                                itemsValidation={itemsValidation}
                                setItemsValidation={(value) => {
                                    const newItemsValidation = itemsValidation.map((item) => (item.idGlobal === value[0].idGlobal ? { ...item, ...value } : item));
                                    setItemsValidation(newItemsValidation);
                                }}
                                gridId={uuidv4()}
                                items={data?.items}
                                data={displayItems}
                                documentType={documentType}
                                sysOptionsKizValidationType={sysOptionsKizValidationType}
                                autoSelect
                                hiddenPagination={undefined}
                                filter={gridFilter}
                                totalCount={displayItems.length}
                                getStyleCell={(cell, cells) => getStyleCell(cell, cells, documentType)}
                                plugin={InvoiceCreatorItemGridPluginSettings}
                                selectedItem={selectedItem}
                                onColumnEnter={(row: IChangeDataGrid) => clearTimer(() => calcRow(row))}
                                onChangeData={(row: IChangeDataGrid) => setTimer(() => calcRow(row))}
                                onSelect={(row) => {
                                    setSelectedItem(row)
                                    selectedItemRef.current = row
                                }}
                                onDoubleClick={(row) => props.onDoubleClick?.(row)}
                                onSort={(i) => dispatchGridFilter({ type: "sort", payload: i.propertyName })}
                                onFilterDelete={(i) => dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName })}
                                onPageNumberChange={(n) => dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                                onNumberPerPageChange={(n) => dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
                                searching
                                mapDisabledField={disabledGosContract ? gosContractMap : undefined}
                                getStyleRow={(item) => {
                                    const cell = item.cells.find(x => x.propertyName === 'isGnvls');
                                    return ((cell?.value === "Да" || cell?.value === true ) && coloredRowIsGnvls) ? stylesIndex.internetOrderNumberCells : null;
                                }}                                
                            />
                        </div>
                        <Accordion opened={false} caption="" id={`accompanyingInvoiceInfo-${uuidv4()}`} bold documentSticky>
                            <GridWrapper cols={3}>
                                <MoneyFormatComponent disabled label={t("documents.invoice.averageMarkup")} inputId={"inputAccompanyingAverageMarkup"} value={+averageMarkup} /> {/*Средняя наценка*/}
                            </GridWrapper>
                            <GridWrapper cols={3}>
                                <MoneyFormatComponent disabled label={t("documents.invoice.supplierVatAmounts")} inputId={"inputAccompanyingNdsSum"} value={sum.amountSupplierVatSum} />
                                <MoneyFormatComponent
                                    disabled
                                    label={t("documents.invoice.supplierAmountsWithoutVat")}
                                    inputId={"inputAccompanyingNdsProviderSum"}
                                    value={Number(sum.amountSupplierSum)}
                                />
                                <MoneyFormatComponent disabled label={t("documents.invoice.supplierAmounts")} inputId={"inputAccompanyingProviderSum"} value={(sum.amountSupplierSumIncVat)} />
                            </GridWrapper>
                            <GridWrapper cols={3}>
                                <MoneyFormatComponent disabled label={t("documents.invoice.retailAmountWithoutVat")} inputId={"inputAccompanyingRetailSumBNds"} value={(sum.amountRetailSum)} />
                                <MoneyFormatComponent disabled label={t("documents.invoice.retailVatAmount")} inputId={"inputAccompanyingNdsRetailSumNds"} value={(sum.amountRetailVatSum)} />
                                <MoneyFormatComponent disabled label={t("documents.invoice.retailAmount")} inputId={"inputAccompanyingRetailSum"} value={(sum.amountRetailSumIncVat)} />
                            </GridWrapper>
                        </Accordion>
                    </div>
                </div>

                {showKizScan && kizScanModal}

                {showMessageModal.show && (
                    <MessageModalWindow
                        message={showMessageModal.message}
                        ok={{
                            onClick: () => {
                                setShowMessageModal({ show: false, message: "" });
                                if (showMessageModal.btSaveOrOk === "ok") {
                                    lockingDocuments.delete({
                                        idTable: idTable,
                                        idDocument: data.idGlobal as string,
                                    });
                                    handleOk(data);
                                } else {
                                    handleSave(data);
                                }
                            },
                        }}
                        cancel={{ onClick: () => setShowMessageModal({ show: false, message: "" }) }}
                    />
                )}

                {showMessageModalValidDocument.show && (
                    <MessageModalWindow
                        message={showMessageModalValidDocument.message}
                        ok={{
                            onClick: () => {
                                setShowMessageModalValidDocument({ show: false, message: "" });
                            },
                        }}
                        cancel={{ onClick: () => setShowMessageModalValidDocument({ show: false, message: "" }) }}
                    />
                )}

                  {showMessageModalGosContractGoods.show && (
                    <GosContractGoodsSelectorModal
                        ok={(entity) => {
                            openTab(showMessageModalGosContractGoods.entity!, 
                                {   
                                    isGosContract: true, 
                                    referentPrice: showMessageModalGosContractGoods.referentPrice,
                                    gosContractGoods: showMessageModalGosContractGoods.gosContractGoods?.find(c => c.idGlobal == entity.idGlobal) ?? null, 
                                    idGosContractGlobal: data.idGosContractExternalGlobal!
                                });
                            setShowMessageModalGosContractGoods({ show: false, message: "" })
                        }}
                        message={showMessageModalGosContractGoods.message}
                        cancel={() => setShowMessageModalGosContractGoods({ show: false, message: "" })} 
                        idGosContractGlobal={showMessageModalGosContractGoods.idGosContractGlobal!} 
                        idGoodsGlobal={showMessageModalGosContractGoods.idGoodsGlobal!}
                        />
                )}

                {goodsViewItem.item && goodsViewItem.show && (
                    <InvoiceGoodsSelectModal
                        ok={(value: IEntitySimpleDTO) => {
                            callOpenInvoiceItemCreatorPlugin(value);
                        }}
                        cancel={() => setGoodsViewItem({ item: goodsViewItem.item, show: false })}
                        data={goodsViewItem.item}
                    />
                )}
                {
                    itemsByGtin.isShowModal() && data.idGlobal &&
                        <InvoiceItemsSelectorModal 
                        idGlobalInvoice={data.idGlobal as string} 
                        gridFilter={{
                            numberPerPage: 100,
                            pageNumber: 1,
                            columnFilters: [{
                                name: "gtin",
                                operator: "Eq",
                                value: itemsDocumentByGtin?.kizParsedDto?.kizBoxData?.gtin ?? itemsDocumentByGtin?.kizParsedDto?.kizData?.gtin
                             } as IGridColumnFilter]
                        } as unknown as IGridFilter}
                        ok={(entities) => {     
                            itemsByGtin.getKiz(entities.idGlobal as string,data.items, (e) => {
                                setKizByGtin(e)
                                itemsByGtin.setShowModal(false)
                            })
                        }} 
                        cancel={() => itemsByGtin.setShowModal(false)}
                />
                }

            </BaseDocumentCreator>
        </>
    );
};

export default InvoiceCreatorForm;
